/**
 * CensusSummary — how many patients are on the ward, and where they are, as a
 * compact strip above the census list.
 *
 * It is a strip and not a panel on purpose. This started as two large tiles
 * filling the board's whole working pane, which meant the biggest region of
 * the surface showed a number and offered no action — the pane now holds the
 * register flow (the only thing a nurse can do with nobody selected) and the
 * tally sits where a tally belongs: above the list it counts, the way the
 * allocation board puts its progress above the beds it tracks.
 *
 * PLATFORM, not the agent's: a ward census is a capability, and the main app
 * renders this through the patient-admin face it bundles.
 *
 * Four states, including the one that was missing: a FAILED list fetch must
 * not fall through to `patients?.length ?? 0` and assert "0 patients" — the
 * board's list pane is showing a retry at that moment, and a confident zero
 * beside it is the surface contradicting itself about whether a ward is empty.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { StaleDataNotice } from './StaleDataNotice'
import type { PatientResponse } from '@/api/endpoints/patients'
import type { Location } from '@/api/endpoints/org'

/** Wards listed individually before the rest collapse into a tally — a
 *  presentation cap, not a domain limit. */
const TOP_WARDS = 4

export interface CensusSummaryProps {
  patients: PatientResponse[] | undefined
  isLoading: boolean
  /** A failed fetch with no cached data — renders "unavailable", never 0. */
  isError: boolean
  /** A background refetch in flight or one that just failed with cached data
   *  still showing — renders a visible stale notice above the tally, never
   *  silently. Optional: omit for callers with no background-refetch signal;
   *  the tally still renders, it just cannot flag mid-flight staleness. */
  isFetching?: boolean
  /** Resolves each patient's `location_id` (their PLACEMENT) to a ward name. */
  locationById: Map<string, Location>
  className?: string
}

export function CensusSummary({
  patients,
  isLoading,
  isError,
  isFetching = false,
  locationById,
  className,
}: CensusSummaryProps) {
  const { t } = useTranslation()

  const wardCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const p of patients ?? []) {
      const ward = p.location_id ? locationById.get(p.location_id)?.name : undefined
      if (ward) counts.set(ward, (counts.get(ward) ?? 0) + 1)
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1])
  }, [patients, locationById])

  // ERROR without cached data — say so; never derive a count from `undefined`.
  if (isError && !patients) {
    return (
      <p
        role="status"
        className={cn('text-[13px] text-neutral-500', className)}
        data-testid="census-summary-unavailable"
      >
        {t('patientAdmin.overview.unavailable')}
      </p>
    )
  }

  // LOADING without cached data — a skeleton the width of the real strip.
  if (isLoading && !patients) {
    return <Skeleton className={cn('h-5 w-48', className)} />
  }

  const total = patients?.length ?? 0
  const topWards = wardCounts.slice(0, TOP_WARDS)
  const otherWardCount = wardCounts.length - topWards.length

  return (
    <div className={className}>
      {/* A background refetch failing (or in flight) with cached counts
          still showing must stay visible — two halves of the screen
          silently disagreeing about freshness is the same trust problem
          the isError-without-data guard above already treats seriously. */}
      <StaleDataNotice isFetching={isFetching} isError={isError} className="mb-1" />
      <div
        className="flex flex-wrap items-baseline gap-x-3 gap-y-1"
        data-testid="census-summary"
      >
        <p className="text-[13px] text-neutral-600">
          <span className="font-display text-[15px] font-semibold tabular-nums tracking-tight text-neutral-900">
            {total}
          </span>{' '}
          {t('patientAdmin.overview.totalPatients')}
        </p>
        {topWards.length > 0 && (
          <ul className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            {topWards.map(([ward, count]) => (
              <li key={ward} className="text-[13px] text-neutral-500">
                <span className="truncate">{ward}</span>{' '}
                <span className="font-semibold tabular-nums text-neutral-700">{count}</span>
              </li>
            ))}
            {otherWardCount > 0 && (
              <li className="text-[13px] text-neutral-400">
                {t('patientAdmin.overview.otherWards', { count: otherWardCount })}
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  )
}
